import { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Spin,
  Alert,
  Row,
  Col,
  Typography,
  Tag,
  message,
  List,
  Tooltip,
} from 'antd';
import { SaveOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useAuth } from '../auth/AuthContext';
import { getMe, updateMyProfile, changeMyPassword, MeResponse } from '../services/adminService';
import PageHeader from '../components/PageHeader';
import PageHelp from '../components/PageHelp';
import MfaSetup from '../components/MfaSetup';

const { Text } = Typography;

// Short label from a User-Agent string. ponytail: first-match heuristic; a UA
// parser library if we ever need OS/device detail.
export function browserName(ua: string | null): string {
  if (!ua) return 'Unknown browser';
  if (/Edg\//.test(ua)) return 'Edge';
  if (/OPR\//.test(ua)) return 'Opera';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua)) return 'Safari';
  return ua.split(' ')[0];
}

type DetailsForm = {
  firstName: string;
  lastName: string;
};

type PasswordForm = {
  previousPassword: string;
  proposedPassword: string;
  confirmPassword: string;
};

function Profile() {
  const auth = useAuth();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [detailsForm] = Form.useForm<DetailsForm>();
  const [passwordForm] = Form.useForm<PasswordForm>();
  const [savingDetails, setSavingDetails] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [replacingMfa, setReplacingMfa] = useState(false);

  const loadProfile = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getMe();
      setMe(data);
      detailsForm.setFieldsValue({
        firstName: data.user.firstName || '',
        lastName: data.user.lastName || '',
      });
    } catch (err) {
      console.error('Profile load error:', err);
      setLoadError('Could not load your profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.authenticated && !auth.isLoading) {
      loadProfile();
    }
    // eslint-disable-next-line
  }, [auth.authenticated, auth.isLoading]);

  const handleSaveDetails = async (values: DetailsForm) => {
    setDetailsError(null);
    setSavingDetails(true);
    try {
      const result = await updateMyProfile({
        firstName: values.firstName,
        lastName: values.lastName,
      });
      message.success('Profile updated');
      setMe(prev =>
        prev
          ? {
              ...prev,
              user: { ...prev.user, firstName: result.firstName, lastName: result.lastName },
            }
          : prev
      );
      const displayName = [result.firstName, result.lastName].filter(Boolean).join(' ').trim();
      if (displayName) auth.setDisplayName(displayName);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update profile';
      const clean = errorMsg.replace(/^API Error: \d+ [^-]+ - /, '');
      let display = 'Failed to update profile';
      try {
        const parsed = JSON.parse(clean);
        if (parsed.error) display = parsed.error;
      } catch {
        // fall back to default
      }
      setDetailsError(display);
      message.error(display);
    } finally {
      setSavingDetails(false);
    }
  };

  const handleChangePassword = async (values: PasswordForm) => {
    setPasswordError(null);
    setSavingPassword(true);
    try {
      await changeMyPassword({
        previousPassword: values.previousPassword,
        proposedPassword: values.proposedPassword,
      });
      message.success('Password changed');
      passwordForm.resetFields();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to change password';
      const clean = errorMsg.replace(/^API Error: \d+ [^-]+ - /, '');
      let display = 'Failed to change password';
      try {
        const parsed = JSON.parse(clean);
        if (parsed.error) display = parsed.error;
      } catch {
        // fall back to default
      }
      setPasswordError(display);
      message.error(display);
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <Spin spinning={loading}>
      <PageHeader
        title="My Profile"
        actions={
          <PageHelp
            title="My Profile Help"
            sections={[
              {
                title: 'Personal details',
                content:
                  'Update your first and last name. Changes are reflected across the application after your next sign-in.',
              },
              {
                title: 'Change password',
                content:
                  'Provide your current password and a new password of at least 8 characters. New password must differ from the current one.',
              },
            ]}
          />
        }
      />

      {loadError && (
        <Alert
          type="error"
          message={loadError}
          showIcon
          action={
            <Button size="small" onClick={loadProfile}>
              Retry
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Personal details">
            {me && (
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">Email:</Text> <Text strong>{me.user.email}</Text>
                <br />
                <Text type="secondary">Username:</Text>{' '}
                <Text code>{auth.cognitoUsername || '—'}</Text>
                <br />
                <Text type="secondary">Role:</Text> <Tag color="blue">{me.user.role}</Tag>
                <Text type="secondary">Organization:</Text> <Text>{me.organization.name}</Text>
              </div>
            )}
            <Form<DetailsForm>
              form={detailsForm}
              layout="vertical"
              onFinish={handleSaveDetails}
              onValuesChange={() => {
                if (detailsError) setDetailsError(null);
              }}
              disabled={loading || !me}
            >
              <Form.Item
                name="firstName"
                label="First name"
                rules={[
                  { required: true, message: 'First name is required' },
                  { max: 100, message: 'Max 100 characters' },
                ]}
              >
                <Input autoComplete="given-name" />
              </Form.Item>
              <Form.Item
                name="lastName"
                label="Last name"
                rules={[
                  { required: true, message: 'Last name is required' },
                  { max: 100, message: 'Max 100 characters' },
                ]}
              >
                <Input autoComplete="family-name" />
              </Form.Item>
              {detailsError && (
                <Alert type="error" message={detailsError} showIcon style={{ marginBottom: 16 }} />
              )}
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={savingDetails}
              >
                Save changes
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Change password">
            <Form<PasswordForm>
              form={passwordForm}
              layout="vertical"
              onFinish={handleChangePassword}
              onValuesChange={() => {
                if (passwordError) setPasswordError(null);
              }}
            >
              <Form.Item
                name="previousPassword"
                label="Current password"
                rules={[{ required: true, message: 'Current password is required' }]}
              >
                <Input.Password autoComplete="current-password" />
              </Form.Item>
              <Form.Item
                name="proposedPassword"
                label="New password"
                rules={[
                  { required: true, message: 'New password is required' },
                  { min: 8, message: 'Minimum 8 characters' },
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                label="Confirm new password"
                dependencies={['proposedPassword']}
                rules={[
                  { required: true, message: 'Please confirm the new password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('proposedPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              {passwordError && (
                <Alert type="error" message={passwordError} showIcon style={{ marginBottom: 16 }} />
              )}
              <Button
                type="primary"
                htmlType="submit"
                icon={<LockOutlined />}
                loading={savingPassword}
              >
                Change password
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Authenticator app">
            {replacingMfa ? (
              <MfaSetup
                email={me?.user.email || ''}
                onDone={() => setReplacingMfa(false)}
                onCancel={() => setReplacingMfa(false)}
              />
            ) : (
              <>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  A code from your authenticator app is required at every sign-in. Replace it here
                  if you have a new phone. The old app stops working once the new one is confirmed.
                </Text>
                <Button icon={<SafetyOutlined />} onClick={() => setReplacingMfa(true)}>
                  Replace authenticator
                </Button>
              </>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Recent sign-ins">
            <List
              size="small"
              dataSource={me?.recent_logins || []}
              locale={{ emptyText: 'No sign-ins recorded yet' }}
              renderItem={login => (
                <List.Item>
                  <Text>{new Date(login.at).toLocaleString()}</Text>
                  <Text type="secondary">{login.ip || '—'}</Text>
                  <Tooltip title={login.user_agent || undefined}>
                    <Text type="secondary" ellipsis style={{ maxWidth: 200 }}>
                      {browserName(login.user_agent)}
                    </Text>
                  </Tooltip>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </Spin>
  );
}

export default Profile;
